/**
 * Inbound lockbox remittance for one bank connection.
 *
 * Written against the bank's own anonymised samples (2026-09-01) rather
 * than the 2015-era spec examples the first draft used. Still expect it
 * to change once OUR records arrive: the samples settle the shape and
 * say nothing about the values, and the payer file's manager UI came
 * out the opposite of what would have been built without running it.
 *
 * WHY THIS IS CONNECTION-SCOPED rather than per-association: a record
 * whose association did not resolve has no HOA to be filed under, so it
 * cannot appear in any association's review list. This is the only place
 * it is visible. Records that DID name an association also surface in
 * that association's review queue, which is where a manager working one
 * HOA will find them.
 *
 * POSTING. Most records post by themselves — the daily job puts anything
 * it can attribute with confidence straight onto the ledger, because
 * holding a payment that arrived is not the cautious option: it leaves
 * the payer's balance wrong and the billing jobs then charge a late fee
 * against it. What reaches this screen as `held` is what the job would
 * not decide alone, and the Post button is the exit. It refuses the
 * holds a person cannot clear by looking — see `blocks_manual_post`.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Heading, Subheading } from './common/Catalyst/heading';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './common/Catalyst/table';
import { Badge } from './common/Catalyst/badge';
import { Text } from './common/Catalyst/text';
import { ErrorAlert } from './common/Custom/ErrorAlert';
import { Button } from './common/Catalyst/button';
import { bankService } from './clients/ApiClients';
import {
  RemittanceList,
  RemittanceRecordReturn,
} from './clients/rust-api-client';
import { formatDate, getBankErrorParts } from './utils/bankHelpers';

/**
 * The four values `match_status` carries.
 *
 * Kept as a local union even though the generated `RemittanceRecordReturn`
 * types the field as `string`: the backend stores it as a string rather
 * than an enum, so utoipa publishes `string` and the generator can only
 * emit that. The union is what makes the two lookup tables below
 * exhaustive — `Record<string, …>` would accept a table missing a case.
 *
 * If the backend ever gains a fifth value, `matchInfo` below degrades to
 * showing it verbatim rather than crashing.
 */
type MatchStatus = 'resolved' | 'hoa_only' | 'unresolved' | 'ambiguous';

interface RemittancePanelProps {
  connectionId: string;
}

const MATCH_LABELS: Record<
  MatchStatus,
  { label: string; tone: 'lime' | 'amber' | 'zinc' | 'red' }
> = {
  resolved: { label: 'Matched', tone: 'lime' },
  hoa_only: { label: 'No matching home', tone: 'amber' },
  unresolved: { label: 'Unknown association', tone: 'zinc' },
  ambiguous: { label: 'Conflicting match', tone: 'red' },
};

/** What a person should do about each state, in their words. */
const MATCH_HELP: Record<MatchStatus, string> = {
  resolved: 'Matched to a home. Nothing to do.',
  hoa_only:
    'The association is ours but the account number is not one we ' +
    'recognise. Usually a number that changed at a sale, or a typo on ' +
    'the coupon.',
  unresolved:
    'We do not recognise the association this names. Worth asking the ' +
    'bank about before anything else — it may not be our money.',
  ambiguous:
    'The association it names and the home its account number resolves ' +
    'to disagree. Do not post this without checking which is right.',
};

/**
 * Label, tone and guidance for a record's match status.
 *
 * Defensive on purpose. The wire value is a `string`, so an unrecognised
 * one is possible whatever the types say — and the previous code indexed
 * the tables directly, which would have read `undefined.label` and taken
 * the whole panel down. Showing an unknown status verbatim is the useful
 * failure: it names the thing to go and look at.
 */
const matchInfo = (
  status: string
): { label: string; tone: 'lime' | 'amber' | 'zinc' | 'red'; help: string } => {
  const known = MATCH_LABELS[status as MatchStatus];
  if (!known) {
    return {
      label: status,
      tone: 'zinc',
      help: 'A status this app does not recognise. Worth reporting.',
    };
  }
  return { ...known, help: MATCH_HELP[status as MatchStatus] };
};

/**
 * Whether to offer a Post button for this record.
 *
 * **A hint, not the rule.** The server decides — `blocks_manual_post`
 * refuses anything a person cannot clear by looking, and it can see
 * things this cannot: the detail string's second rendering of the
 * amount is not on the wire, so a record whose two amounts disagree
 * looks postable here and is refused there.
 *
 * That asymmetry is deliberate rather than sloppy. Duplicating the rule
 * in TypeScript would mean two copies of the only thing standing
 * between the lockbox and a ledger, and the copies would drift. So this
 * hides the button where it would obviously fail — already posted, no
 * home, no readable amount — and lets the server answer the rest with a
 * reason a person can read.
 */
const canPost = (r: RemittanceRecordReturn): boolean =>
  r.payment_id == null &&
  r.match_status === 'resolved' &&
  r.item_amount != null &&
  Number(r.item_amount) > 0;

const RemittancePanel: React.FC<RemittancePanelProps> = ({ connectionId }) => {
  const [data, setData] = useState<RemittanceList | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unmatchedOnly, setUnmatchedOnly] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [posting, setPosting] = useState<string | null>(null);
  const [postError, setPostError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await bankService.listConnectionRemittance({
        connectionId,
        unmatchedOnly,
      });
      setData(response.data);
    } catch (e) {
      const { body } = getBankErrorParts(e);
      setError(body || "Couldn't load remittance records.");
    } finally {
      setLoading(false);
    }
  }, [connectionId, unmatchedOnly]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Post one held record.
   *
   * Reloads the whole list afterwards rather than patching the row in
   * place: posting can change a record's answer to more than its own
   * status — the payment it creates may be what a later record applies
   * against — and a hand-patched row would show a stale view of a
   * ledger that just moved.
   */
  const post = useCallback(
    async (recordId: string) => {
      setPosting(recordId);
      setPostError(null);
      try {
        await bankService.postRemittanceRecord({ recordId });
        await load();
      } catch (e) {
        const { body } = getBankErrorParts(e);
        setPostError(body || "Couldn't post that record.");
      } finally {
        setPosting(null);
      }
    },
    [load]
  );

  const neverIngested = useMemo(
    () => data != null && data.synced_through == null,
    [data]
  );

  return (
    <div className="mt-8">
      <div className="flex items-end justify-between gap-4">
        <div>
          <Heading level={2}>Lockbox payments</Heading>
          <Text className="mt-1">
            Payments the lockbox matched to a payer row and reported back.
            Anything confidently matched posts by itself; what is held below is
            waiting on a person.
          </Text>
        </div>
        <Button
          outline
          onClick={() => setUnmatchedOnly((v) => !v)}
          disabled={loading}
        >
          {unmatchedOnly ? 'Show all' : 'Show only unmatched'}
        </Button>
      </div>

      <ErrorAlert className="mt-4">{error}</ErrorAlert>

      {/* The server's reason for refusing a post, verbatim. It names
          the hold — `amount_disagrees`, `unmatched` — which is the
          same vocabulary the badge uses, so the refusal and the row
          agree with each other. */}
      {postError && (
        <ErrorAlert className="mt-4" title="Couldn't post">
          {postError}
        </ErrorAlert>
      )}

      {/*
        Three empty states, and they mean different things. Collapsing
        them into one "nothing here" is how "we never looked" becomes
        indistinguishable from "nothing arrived" — the mistake the
        transaction pull's read was designed around.
      */}
      {!loading && !error && neverIngested && (
        <Text className="mt-4">
          This connection has not been read yet. The daily job has not run for
          it, so this is not the same as no payments having arrived.
        </Text>
      )}

      {!loading && !error && !neverIngested && data?.total === 0 && (
        <Text className="mt-4">
          {unmatchedOnly
            ? `Nothing needs attention. Read through ${formatDate(
                data.synced_through
              )}.`
            : `No payments have come through the lockbox. Read through ${formatDate(
                data.synced_through
              )}.`}
        </Text>
      )}

      {!loading && !error && data != null && data.total > 0 && (
        <>
          <Subheading className="mt-6">
            {data.total} record{data.total === 1 ? '' : 's'} · read through{' '}
            {formatDate(data.synced_through)}
          </Subheading>
          <Table className="mt-2">
            <TableHead>
              <TableRow>
                <TableHeader>Processed</TableHeader>
                <TableHeader>Amount</TableHeader>
                {/* Explicitly the PAYER's number. The connection's own
                    depository account is deliberately not a column —
                    it is identical on every row and would invite
                    exactly the confusion that puts a whole batch on
                    one home. */}
                <TableHeader>Payer account</TableHeader>
                <TableHeader>Association</TableHeader>
                <TableHeader>Match</TableHeader>
                <TableHeader>Lockbox said</TableHeader>
                <TableHeader>Posted</TableHeader>
                <TableHeader />
              </TableRow>
            </TableHead>
            <TableBody>
              {data.records.map((r) => {
                const match = matchInfo(r.match_status);
                const isOpen = expanded === r.id;
                return (
                  <React.Fragment key={r.id}>
                    <TableRow>
                      <TableCell>{formatDate(r.processed_on)}</TableCell>
                      <TableCell>
                        {r.item_amount ? (
                          `$${r.item_amount}`
                        ) : (
                          // An amount we could not read is a defect,
                          // not an unusual payment, and it should look
                          // like one.
                          <Badge color="red">
                            unreadable
                            {r.item_amount_raw ? `: ${r.item_amount_raw}` : ''}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {r.payer_account_number ?? '—'}
                        {/* The payer's name, where the source sends
                            one. An account number that resolved to
                            nothing plus a name is a question a manager
                            can answer; an account number alone usually
                            is not. */}
                        {r.payer_customer_name && (
                          <div className="font-sans text-zinc-500">
                            {r.payer_customer_name}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {r.payer_association_id ?? '—'}
                        {/* How the LOCKBOX spells it, which is not
                            necessarily how we do. Shown to help
                            recognise the record, never used to resolve
                            one. */}
                        {r.payer_association_name && (
                          <div className="font-sans text-zinc-500">
                            {r.payer_association_name}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge color={match.tone}>{match.label}</Badge>
                      </TableCell>
                      <TableCell>
                        {/* Verbatim, never translated. The two
                            documented vocabularies for this field
                            disagree, so showing our interpretation of
                            it would be showing a guess. */}
                        {r.match_type ?? '—'}
                      </TableCell>
                      <TableCell>
                        {r.post_status === 'posted' ? (
                          <Badge color="green">posted</Badge>
                        ) : r.post_status === 'held' ? (
                          <Badge color="amber">{r.hold_reason ?? 'held'}</Badge>
                        ) : (
                          <Badge color="zinc">{r.post_status}</Badge>
                        )}
                        {/* A payment that came from a mock rather than
                            from the bank. Absent on every real record;
                            when present it is the ONLY thing on the
                            page separating money that arrived from
                            money somebody invented to test with. */}
                        {r.mock_source_url && (
                          <div className="mt-1">
                            <Badge color="red">not real money</Badge>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-2">
                          {canPost(r) && (
                            <Button
                              onClick={() => void post(r.id)}
                              disabled={posting != null}
                            >
                              {posting === r.id ? 'Posting…' : 'Post'}
                            </Button>
                          )}
                          <Button
                            plain
                            onClick={() => setExpanded(isOpen ? null : r.id)}
                          >
                            {isOpen ? 'Hide' : 'Details'}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    {isOpen && (
                      <TableRow>
                        <TableCell colSpan={8}>
                          <div className="space-y-2 py-2 text-sm">
                            <Text>{match.help}</Text>
                            {r.unknown_detail_keys && (
                              // NO LONGER A WARNING. Real records carry
                              // several keys we do not read, so an
                              // amber line here fired on every row and
                              // meant nothing. The signal moved to a
                              // MISSING match key, which shows up as an
                              // unresolved match status instead.
                              <Text className="text-zinc-500">
                                Also sent: {r.unknown_detail_keys}
                              </Text>
                            )}
                            <Text className="font-mono text-xs">
                              Bank record {r.provider_record_id}
                              {r.deposit_account_number
                                ? ` · deposited to ${r.deposit_account_number}`
                                : ''}
                            </Text>
                            {r.raw_detail && (
                              <pre className="overflow-x-auto rounded bg-zinc-100 p-2 text-xs dark:bg-zinc-800">
                                {r.raw_detail}
                              </pre>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </>
      )}
    </div>
  );
};

export default RemittancePanel;
